import React, { useState } from 'react';
import * as ReactDOMClient from 'react-dom/client';
import KaModal from '../Common/KaModal';
import KaAlert from '../Common/KaAlert';
import SpinnerButton from '../Common/SpinnerButton';
import useDisplayMessage from '../Common/useDisplayMessage';
import useGetProductConfigurationReport from '../../requests/useGetProductConfigurationReport';
import { ProductConfigurationReportDisplay, reportColumns } from './ProductConfigurationReportDisplay';
import { KaSelect, toKaSelectOptions, isMultiValue, KaSelectOption } from '../Common/KaSelect';
import { Row, Col, Label } from 'reactstrap';
import useGetSitesNonAdmin from '../Requests/useGetSitesNonAdmin';
import useGetProducts from '../Requests/useGetProducts';
import ProductDto from '../Common/dtos/ProductDto';
import MultiQueryView from '../Common/MultiQueryView';
import { useDownloadProductConfigurationReport } from '../../requests/useDownloadProductConfigurationReport';
import { SingleValue } from 'react-select';

interface ProductConfigurationReportModalProps {
  show: boolean;
  onHide: () => void;
}

export const ProductConfigurationReportModal = ({ show, onHide }: ProductConfigurationReportModalProps) => {
  const displayMessage = useDisplayMessage();
  const columnOptions = reportColumns.map(rc => ({ value: rc, label: rc }));

  const [selectedSiteId, setSelectedSiteId] = useState<string>("");
  const [selectedSiteName, setSelectedSiteName] = useState<string>("");
  const [selectedProductId, setSelectedProductId] = useState<string>("");
  const [selectedProductName, setSelectedProductName] = useState<string>("");
  const [selectedColumns, setSelectedColumns] = useState<KaSelectOption[]>(columnOptions);

  const getSites = useGetSitesNonAdmin();
  const getProducts = useGetProducts();

  const anyQueriesLoading = getSites.isLoading || getProducts.isLoading;

  const queries = {
    sites: getSites,
    products: getProducts,
  }

  const handleClose = () => {
    onHide();
    displayMessage.clear();
    setSelectedSiteId("");
    setSelectedSiteName("");
    setSelectedProductId("");
    setSelectedProductName("");
  };

  const params = {
    siteId: selectedSiteId,
    siteName: selectedSiteName,
    productId: selectedProductId,
    productName: selectedProductName,
  };

  const downloadQuery = useDownloadProductConfigurationReport(params);

  const reportQuery = useGetProductConfigurationReport(
    params,
    {
      onSuccess: (reportData) => {
        const reportWindow = window.open('', '_blank');
        if (!reportWindow) {
          displayMessage.fail('Unable to open report window. Please check your popup blocker settings.');
          return;
        }

        reportWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>Product Configuration Report</title>
              <link href="${window.location.origin}/assets/vendor/bootstrap-icons/font/bootstrap-icons.css" rel="stylesheet">
              <link href="${window.location.origin}/assets/css/theme.min.css" rel="stylesheet">
              <link href="${window.location.origin}/assets/css/vendor.min.css" rel="stylesheet">
              <link href="${window.location.origin}/custom.css" rel="stylesheet">
            </head>
            <body>
              <div id="report-root"></div>
            </body>
          </html>
        `);
        reportWindow.document.close();

        const container = reportWindow.document.getElementById('report-root')!;
        const root = ReactDOMClient.createRoot(container);
        root.render(<ProductConfigurationReportDisplay reportData={reportData} downloadQuery={downloadQuery} columns={selectedColumns.map(c => c.value)} />);

        handleClose();
      },
      onError: (error) => {
        displayMessage.fail(error.message || 'Failed to generate report');
      }
    }
  );

  const listOfProducts = (products: ProductDto[]) =>
    products.map(p => ({
      value: p.id,
      label: p.name,
    }));

  const products = listOfProducts(getProducts.data ?? []);
  const productOptions = toKaSelectOptions<KaSelectOption, KaSelectOption>(
    products,
    "value",
    "label",
    { sortByLabel: true }
  );

  const siteOptions = toKaSelectOptions(getSites.data ?? [], "id", "name", { sortByLabel: true });

  const handleSiteChanged = (option: SingleValue<KaSelectOption>) => {
    setSelectedSiteId(option?.value || "");
    setSelectedSiteName(option?.label || "");
  }

  const handleProductChanged = (option: SingleValue<KaSelectOption>) => {
    setSelectedProductId(option?.value || "");
    setSelectedProductName(option?.label || "");
  }

  const handleGenerateReport = () => {
    displayMessage.clear();
    reportQuery.query();
  };

  return (
    <KaModal
      show={show}
      onHide={handleClose}
      title="Product Configuration Report"
      body={
        <>
          <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
          <p>
            Generate a CSV report showing all products configured in Constellation, including site assignments amd defined density values, to help verify product setup across systems.
          </p>
          <MultiQueryView
            queries={queries}
            renderData={_ => <>
              <h5 className="mb-2">
                Show Columns
              </h5>
              <Row className="mb-5">
                <Col>
                  <KaSelect
                    placeholder="Select Columns"
                    options={columnOptions}
                    isClearable={true}
                    closeMenuOnSelect={false}
                    isMulti={true}
                    onChange={(valueObject) => {
                      if (isMultiValue(valueObject)) { setSelectedColumns(valueObject.map(c => c as KaSelectOption)) }
                    }}
                    value={selectedColumns} />
                </Col>
              </Row>
              <h5 className="mb-2">
                Filter report
              </h5>
              <Row className="mb-2">
                <Col>
                  <Label htmlFor="site-selector" className="form-label">
                    Site:
                  </Label>
                  <KaSelect
                    inputId="site-selector"
                    options={siteOptions}
                    value={siteOptions.find(option => option.value === selectedSiteId)}
                    onChange={(option) => handleSiteChanged(isMultiValue(option) ? null : option)}
                    placeholder="Select site"
                    isClearable={true}
                  />
                </Col>
              </Row>
              <Row className="mb-2">
                <Col>
                  <Label htmlFor="product-selector" className="form-label">
                    Product:
                  </Label>
                  <KaSelect
                    inputId="product-selector"
                    options={productOptions}
                    value={productOptions.find(option => option.value === selectedProductId)}
                    onChange={(option) => handleProductChanged(isMultiValue(option) ? null : option)}
                    placeholder="Select product"
                    isClearable={true}
                  />
                </Col>
              </Row>
            </>} />
        </>
      }
      footer={!anyQueriesLoading && <>
        <SpinnerButton
          spinning={false}
          onClick={downloadQuery}>
          Download CSV
        </SpinnerButton>
        <SpinnerButton
          spinning={reportQuery.isLoading}
          onClick={handleGenerateReport}>
          View in New Tab
        </SpinnerButton>
      </>
      }
    />
  );
};